import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Offer, OfferCode, OfferRedemption, OwnedPersonalCode, UserRedeemedCode } from "@/types/breeze/AnythinkPay";
import { anythinkPayService } from "@/services/breeze/AnythinkPayService";

export const anythinkPayOfferKeys = {
  all: ["anythinkpay-offers"] as const,
  lists: () => [...anythinkPayOfferKeys.all, "list"] as const,
  list: () => [...anythinkPayOfferKeys.lists()] as const,
  details: () => [...anythinkPayOfferKeys.all, "detail"] as const,
  detail: (id: string) => [...anythinkPayOfferKeys.details(), id] as const,
  redemptions: (id: string, page: number, pageSize: number) =>
    [...anythinkPayOfferKeys.detail(id), "redemptions", { page, pageSize }] as const,
  codes: (id: string) => [...anythinkPayOfferKeys.detail(id), "codes"] as const,
};

export function useAnythinkPayOffers() {
  return useQuery<Offer[]>({
    queryKey: anythinkPayOfferKeys.list(),
    queryFn: () => anythinkPayService.getOffers(),
  });
}

export function useAnythinkPayOffer(id: string | undefined) {
  return useQuery<Offer>({
    queryKey: anythinkPayOfferKeys.detail(id ?? ""),
    queryFn: () => anythinkPayService.getOffer(id!),
    enabled: !!id,
  });
}

export function useAnythinkPayOfferRedemptions(
  id: string | undefined,
  page: number = 1,
  pageSize: number = 50,
) {
  return useQuery<OfferRedemption[]>({
    queryKey: anythinkPayOfferKeys.redemptions(id ?? "", page, pageSize),
    queryFn: () => anythinkPayService.getOfferRedemptions(id!, page, pageSize),
    enabled: !!id,
  });
}

export function useUserBalances(userId: number | undefined) {
  return useQuery<import("@/types/breeze/AnythinkPay").UserBalance[]>({
    queryKey: ["anythinkpay-user-balances", userId],
    queryFn: () => anythinkPayService.getUserBalances(userId!),
    enabled: !!userId,
    staleTime: 30_000,
  });
}

export function useUserBalanceHistory(userId: number | undefined, page = 1, pageSize = 50) {
  return useQuery<import("@/types/breeze/AnythinkPay").UserBalanceLedgerEntry[]>({
    queryKey: ["anythinkpay-user-balance-history", userId, page, pageSize],
    queryFn: () => anythinkPayService.getUserBalanceHistory(userId!, page, pageSize),
    enabled: !!userId,
    staleTime: 30_000,
  });
}

export function useTenantBalanceHolders(unitType: string | undefined, page = 1, pageSize = 50) {
  return useQuery<import("@/types/breeze/AnythinkPay").TenantBalanceHolder[]>({
    queryKey: ["anythinkpay-tenant-balance-holders", unitType ?? "all", page, pageSize],
    queryFn: () => anythinkPayService.getTenantBalanceHolders(unitType, page, pageSize),
    staleTime: 30_000,
  });
}

export function useTenantBalanceActivity(page = 1, pageSize = 50) {
  return useQuery<import("@/types/breeze/AnythinkPay").UserBalanceLedgerEntry[]>({
    queryKey: ["anythinkpay-tenant-balance-activity", page, pageSize],
    queryFn: () => anythinkPayService.getTenantBalanceActivity(page, pageSize),
    staleTime: 30_000,
  });
}

export function useAdjustUserBalance(userId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: import("@/types/breeze/AnythinkPay").AdjustUserBalanceRequest) =>
      anythinkPayService.adjustUserBalance(userId!, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["anythinkpay-user-balances", userId] });
      queryClient.invalidateQueries({ queryKey: ["anythinkpay-user-balance-history", userId] });
      // Tenant-wide holders + activity are derived from the same ledger, so they go stale too.
      queryClient.invalidateQueries({ queryKey: ["anythinkpay-tenant-balance-holders"] });
      queryClient.invalidateQueries({ queryKey: ["anythinkpay-tenant-balance-activity"] });
    },
  });
}

export function useTenantRedemptions(
  kinds: string[],
  page: number = 1,
  pageSize: number = 50,
) {
  const key = [...kinds].sort();
  return useQuery<OfferRedemption[]>({
    queryKey: ["anythinkpay-tenant-redemptions", key, page, pageSize],
    queryFn: () => anythinkPayService.getTenantRedemptions(key, page, pageSize),
    // Redemptions accrue from outside the admin UI; keep this query fresh so the tab feels live.
    staleTime: 30_000,
  });
}

export function useCreateAnythinkPayOffer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: Parameters<typeof anythinkPayService.createOffer>[0]) =>
      anythinkPayService.createOffer(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: anythinkPayOfferKeys.lists() });
    },
  });
}

export function useAnythinkPayOfferCodes(id: string | undefined) {
  return useQuery<OfferCode[]>({
    queryKey: anythinkPayOfferKeys.codes(id ?? ""),
    queryFn: () => anythinkPayService.getOfferCodes(id!),
    enabled: !!id,
  });
}

export function useUsersReferralCodes(userIds: number[]) {
  const key = [...userIds].sort((a, b) => a - b);
  return useQuery<Record<number, OwnedPersonalCode[]>>({
    queryKey: ["anythinkpay-users-referral-codes", key],
    queryFn: async () => {
      const codes = await anythinkPayService.getUsersReferralCodes(key);
      const grouped: Record<number, OwnedPersonalCode[]> = {};
      for (const code of codes) {
        (grouped[code.owner_user_id] ??= []).push(code);
      }
      return grouped;
    },
    enabled: key.length > 0,
    staleTime: Infinity,
  });
}

export function useUsersRedeemedCodes(userIds: number[]) {
  const key = [...userIds].sort((a, b) => a - b);
  return useQuery<Record<number, UserRedeemedCode[]>>({
    queryKey: ["anythinkpay-users-redeemed-codes", key],
    queryFn: async () => {
      const rows = await anythinkPayService.getUsersRedeemedCodes(key);
      const grouped: Record<number, UserRedeemedCode[]> = {};
      for (const row of rows) {
        (grouped[row.redeemer_user_id] ??= []).push(row);
      }
      return grouped;
    },
    enabled: key.length > 0,
    // Shorter than the referral-codes hook because redemptions accrue continually (mobile app
    // hits the redeem endpoint without telling the admin UI). 30s feels right for an admin list.
    staleTime: 30_000,
  });
}

export function useCreateAnythinkPayOfferCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id, ...request
    }: { id: string; slug: string; owner_user_id?: number | null }) =>
      anythinkPayService.createOfferCode(id, request),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: anythinkPayOfferKeys.codes(id) });
    },
  });
}

export function useUpdateAnythinkPayOffer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...request
    }: { id: string } & Parameters<typeof anythinkPayService.updateOffer>[1]) =>
      anythinkPayService.updateOffer(id, request),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: anythinkPayOfferKeys.lists() });
      queryClient.invalidateQueries({ queryKey: anythinkPayOfferKeys.detail(id) });
    },
  });
}
